/**
 * Full version history for a persona — every immutable bundle row,
 * newest first (server orders by version desc). Feeds the Workshop's
 * Version History drawer; fetched only while the drawer is open and
 * invalidated by save/restore (WorkshopContent).
 *
 * `staleTime: 0` overrides the app-wide 5-minute default (main.tsx) —
 * without it, reopening the drawer within that window would silently
 * skip the refetch and show a stale Active row/diffs (e.g. a save from
 * another session/tab). The list is small and only fetched while the
 * drawer is open, so refetch-on-every-open is cheap and correct.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type { PersonaBundleResponse } from '@/types/personaWizard'

export const personaBundleHistoryKey = (personaId: string) =>
  ['persona-bundle-history', personaId] as const

export function usePersonaBundleHistory(
  personaId: string,
  opts: { enabled: boolean },
): UseQueryResult<PersonaBundleResponse[], Error> {
  return useQuery({
    queryKey: personaBundleHistoryKey(personaId),
    enabled: opts.enabled,
    staleTime: 0,
    queryFn: () =>
      apiClient.get<PersonaBundleResponse[]>(
        `/api/persona-bundles?persona_id=${encodeURIComponent(personaId)}`,
      ),
  })
}
